import { useState } from "react";
import { useSupabaseAuth } from "@ugg/shared/components/auth/SupabaseAuthProvider";
import { useAuthCaptcha } from "@ugg/shared/hooks/use-auth-captcha";
import { CAPTCHA_ACTIONS } from "@ugg/shared/utils/captcha";
import { authErrorMessage } from "@ugg/shared/utils/auth-captcha";

type EmailSignupStep = "details" | "code";

export const EMAIL_EXISTS_ERROR = "An account with this email already exists. Log in instead.";

export interface EmailSignupDetails {
  emailAddress: string;
  password: string;
  marketingOptIn: boolean;
}

export function useEmailSignup(onSignedUp: () => void | Promise<void>) {
  const { client } = useSupabaseAuth();
  const captcha = useAuthCaptcha();
  const [step, setStep] = useState<EmailSignupStep>("details");
  const [pendingEmail, setPendingEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const submitDetails = async ({ emailAddress, password, marketingOptIn }: EmailSignupDetails) => {
    setError("");
    setIsLoading(true);
    try {
      const captchaToken = await captcha.mint(CAPTCHA_ACTIONS.register, setError);
      if (!captchaToken) {
        return;
      }
      const { data, error: signUpError } = await client.auth.signUp({
        email: emailAddress,
        password,
        // Stored as an opt-OUT to match the shape the GraphQL API already uses for this field.
        options: { captchaToken, data: { signup_site: "ugg", marketingOptOut: !marketingOptIn } },
      });
      if (signUpError) {
        setError(authErrorMessage(signUpError));
        return;
      }
      if (data.session) {
        onSignedUp();
        return;
      }
      if (data.user?.identities?.length === 0) {
        setError(EMAIL_EXISTS_ERROR);
        return;
      }
      setPendingEmail(emailAddress);
      setStep("code");
    } finally {
      setIsLoading(false);
    }
  };

  const submitCode = async (code: string) => {
    setError("");
    setIsLoading(true);
    try {
      const { error: verifyError } = await client.auth.verifyOtp({ email: pendingEmail, token: code, type: "email" });
      if (verifyError) {
        setError(verifyError.message);
        return;
      }
      await onSignedUp();
    } finally {
      setIsLoading(false);
    }
  };

  const resendCode = async () => {
    setError("");
    const captchaToken = await captcha.mint(CAPTCHA_ACTIONS.resend, setError);
    if (!captchaToken) {
      return;
    }
    const { error: resendError } = await client.auth.resend({ type: "signup", email: pendingEmail, options: { captchaToken } });
    if (resendError) {
      setError(authErrorMessage(resendError));
    }
  };

  const reset = () => {
    captcha.cancel();
    setError("");
    setPendingEmail("");
    setStep("details");
  };

  return { step, error, isLoading, submitDetails, submitCode, resendCode, reset, captchaContainerRef: captcha.containerRef };
}
